import { CheckCircleOutlined } from '@ant-design/icons'
import { Alert, Button, Checkbox, Table } from 'antd'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'
import { CRUD_VIEW_TYPE, VIEW_TYPE } from '../../Types'
import { FormLabel } from '../../components/form/FormLabel'
import { JustificationInput } from '../../components/form/JustificationInput'
import { getFormType } from '../../components/form/formFactory'
import { openNotification } from '../../components/notifications/openNotification'
import { useProject } from '../../hooks/useProject'
import { useProjectConfig } from '../../hooks/useProjectConfig'
import { post } from '../../service/API'
import { ENDPOINTS } from '../../service/ENDPOINTS'
import { populateConfig } from '../../util/populateConfig'
import { getSiteConfig } from './siteConfig'
import { useSiteWithLocalTime } from '../../hooks/useSiteWithLocalTime'

/**
 * Re. Languages
 * New paradigm.
 * ONLY languages that are ACTIVE on a site are saved in the site's language list
 * There will be a backwards compatibility issue since there are sites having languages
 * saved that has an active state set til false and there is surely code that rely on that throughout the system.
 * Hence, the active state MUST be set to be TRUE for languages saved on the site
 *
 * Again:
 * - Only language that are considered to be active on a site are saved in the site's language list
 * - A language's active state must be set to "true" when saved on a site. For backwards compatibility
 * - A site's language list is a subset of a projects language list
 *
 * Languages to display for view types:
 * - CREATE
 * -- We list all the project languages
 * -- The language object is taken from the project language list
 * -- Active state is forced to be false per default
 *
 * - UPDATE
 * -- We list all the project languages
 * -- The language object is taken from the project language list
 * -- Active state is set to true if:
 * --- it is present in the site's language list
 * --- and active state is set to true
 * -- Else the active state is set to be false
 *
 * -VIEW
 * -- We show only the languages saved on the site
 */

type Props = {
  closeDrawer?: (value) => void
  onClose?: () => void
  viewType?: VIEW_TYPE
}

export const SiteCRUD = ({ closeDrawer, onClose, viewType = 'CREATE' }: Props) => {
  const { projectId, siteId } = useParams() as { projectId: string; siteId: string }

  const { data, mutate } = useSiteWithLocalTime(projectId, siteId, !!siteId)
  const { data: dataConfig } = useProjectConfig()
  const { data: projectData } = useProject(projectId)

  // langData is the updated list of language objects eventually submitted to backend
  const [langData, setLangData] = useState<{ isoCode: string; name: string; active: boolean }[]>([])

  const [table, setTable] = useState<
    {
      key: string
      name: string
      activeProject: boolean
      activeSite: boolean
      isoCode: string
    }[]
  >([])

  useEffect(() => {
    if (projectData?.languages) {
      // Create an array of languages objects to be manipulated
      // The array is eventually saved on the site
      // For CREATE and VIEW, use the projects language array as a base
      // For VIEW, use the site's language array directly
      const siteLanguageActivationList =
        viewType === 'CREATE'
          ? projectData?.languages.map((language) => {
              return { ...language, active: false }
            })
          : projectData?.languages.map((language) => {
              const isActive = data?.languages.some((lang) => lang.isoCode === language.isoCode && lang.active) // Checking active state makes it backwards compatible
              return { ...language, active: isActive } // isActive will ONLY be true if the language is present in the language list on the site and if its active state is set to true
            })
      setLangData(siteLanguageActivationList)

      const updatedTable = siteLanguageActivationList?.map((language) => ({
        key: language.isoCode,
        name: language.name,
        activeProject: !!projectData?.languages?.find((lang) => lang.isoCode === language.isoCode && lang.active),
        activeSite: language.active,
        isoCode: language.isoCode,
      }))
      setTable(updatedTable)
    }
  }, [data?.languages, projectData?.languages, viewType])

  const [localSiteDate, localSiteTime] = (data?.localTime || '-, -').split(', ')

  const config =
    viewType === 'UPDATE'
      ? populateConfig(getSiteConfig(dataConfig?.timezones), data, viewType)
      : getSiteConfig(dataConfig?.timezones)

  const formHook = useForm()
  const { handleSubmit } = formHook
  const [errorState, setErrorState] = useState<any>()

  const onSubmit = (formData, e) => {
    e.preventDefault()
    if (!isValidSiteNumber(formData.siteNumber)) {
      setErrorState({
        message: 'Site number can contain the lower case letters a to z, the numbers 0 to 9',
      })
      return
    } else {
      setErrorState(null)
    }

    // Filter out the languages that have the active state set to be true
    const langDataToSend = langData.filter((lang) => lang.active)

    const createData = { ...formData, projectId: projectId, languages: langDataToSend }
    const updatedData = { ...data, ...formData, languages: langDataToSend }
    const method = viewType === 'UPDATE' ? 'PUT' : 'POST'
    const sendData = viewType === 'UPDATE' ? updatedData : createData

    return post(ENDPOINTS.SITES.ACTION(projectId), sendData, method)
      .then((result) => {
        mutate()

        openNotification('Site successfully updated', '', 'happy')
        if (onClose) {
          onClose()
        }
        if (closeDrawer) {
          closeDrawer(false)
        }
      })
      .catch((error) => {
        console.log('error = ', error)
        openNotification('Site update failed: ', error.message, 'sad')
        setErrorState(error)
      })
  }

  const onError = (err, e) => {
    e.preventDefault()
  }

  const navigateBack = () => {
    if (onClose) {
      onClose()
    }
  }

  const isValidSiteNumber = (value: string) => {
    return /^[a-z0-9]+$/.test(value) // /^(?!.*\s{2,})[a-z0-9\s]+$/.test(value)
  }

  const onChange = (isoCode, checked) => {
    // update the language data list
    setLangData(langData.map((lang) => (lang.isoCode === isoCode ? { ...lang, active: checked } : lang)))
  }

  const greenCheckV3 = <CheckCircleOutlined style={{ fontSize: '18px', color: '#526b5b' }} />

  const columns = [
    {
      title: 'Language',
      dataIndex: 'name',
      key: 'name',
      className: 'p-small',
    },
    {
      title: 'Active on Project',
      dataIndex: 'activeProject',
      key: 'activeProject',
      className: 'p-small',
      render: (active, record) => {
        return (
          <div className="ms-2" style={{}}>
            {viewType === 'VIEW' ? active ? greenCheckV3 : <></> : <Checkbox disabled={true} defaultChecked={active} />}
          </div>
        )
      },
    },
    {
      title: 'Active on Site',
      dataIndex: 'activeSite',
      key: 'activeSite',
      className: 'p-small',
      render: (active, record) => {
        return (
          <div className="ms-2" style={{}}>
            {viewType === 'VIEW' ? (
              active ? (
                greenCheckV3
              ) : (
                <></>
              )
            ) : (
              <Checkbox
                defaultChecked={record.activeSite}
                // checked={record.activeSite}
                onChange={(e) => onChange(record.isoCode, e.target.checked)}
              />
            )}
          </div>
        )
      },
    },
  ]

  return (
    <>
      {viewType === 'CREATE' || viewType === 'UPDATE' ? (
        <form className="row" onSubmit={handleSubmit(onSubmit, onError)}>
          <div className="col-12">
            {config.map((item, index) => {
              // -------------------------------------------------------------------------------------
              // If time allows, investigate how to do it directly through the config (by injecting)
              // -------------------------------------------------------------------------------------
              const comp = getFormType(item, index, formHook, [], undefined, dataConfig?.countries)
              return comp
            })}
          </div>
          <div className="mt-2 mb-3">
            <FormLabel label="Site languages"></FormLabel>
            <Table columns={columns} dataSource={table} size="small" pagination={false} bordered />
          </div>
          {/*Justification*/}
          {viewType === CRUD_VIEW_TYPE.UPDATE ? <JustificationInput formHook={formHook} /> : null}
          <div className="col-12 mt-3">
            {errorState ? (
              <Alert showIcon message={errorState.type} description={errorState.message} type="error" />
            ) : (
              <></>
            )}
          </div>
          <div className="col-12 d-flex justify-content-end mt-3">
            <Button className="me-2" onClick={navigateBack}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleSubmit(onSubmit, onError)}>
              {viewType === 'CREATE' ? 'Create site' : 'Save changes'}
            </Button>
          </div>
        </form>
      ) : (
        <div className="">
          <div className="col-12 mb-4">
            <div className="col-12 mb-4">
              <FormLabel label="Site number"></FormLabel>
              <h6 className="fw-bold">{data?.siteNumber || '-'}</h6>
            </div>
            <FormLabel label="Site name"></FormLabel>
            <h6 className="fw-bold">{data?.title || '-'}</h6>
          </div>
          <div className="col-12 mb-4">
            <FormLabel label="Site address"></FormLabel>
            <h6 className="fw-bold">{data?.address || '-'}</h6>
          </div>
          <div className="col-12 mb-4">
            <FormLabel label="Site phone number"></FormLabel>
            <h6 className="fw-bold">{data.phone ? data.phone.countryCode + ' ' + data.phone.tel : '-'}</h6>
          </div>
          <div className="col-12 mb-4">
            <FormLabel label="Site contact person name"></FormLabel>
            <h6 className="fw-bold">{data?.contactPersonName || '-'}</h6>
          </div>
          <div className="col-12 mb-4">
            <FormLabel label="Site contact person email"></FormLabel>
            <h6 className="fw-bold">{data?.contactPersonEmail || '-'}</h6>
          </div>
          <div className="col-12 mb-4">
            <FormLabel label="Site contact person job title"></FormLabel>
            <h6 className="fw-bold">{data?.contactPersonJobTitle || '-'}</h6>
          </div>
          <div className="col-12 mb-4">
            <FormLabel label="Time zone"></FormLabel>
            <h6 className="fw-bold">{data?.timeZone || '-'}</h6>
          </div>
          <div className="col-12 mb-4">
            <FormLabel label="Site local date"></FormLabel>
            <h6 className="fw-bold">{localSiteDate ? localSiteDate : '-'}</h6>
          </div>
          <div className="col-12 mb-4">
            <FormLabel label="Site local time (24-hour clock)"></FormLabel>
            <h6 className="fw-bold">{localSiteTime ? localSiteTime : '-'}</h6>
          </div>
          <div className="col-12 mb-4">
            <FormLabel label="Active"></FormLabel>
            <h6 className="fw-bold">{data?.active === true ? 'Yes' : 'No' || '-'}</h6>
          </div>
          <div className="col-12 mb-4">
            <FormLabel label="Site languages"></FormLabel>
            <Table columns={columns} bordered dataSource={table} size="small" pagination={false} />
          </div>
          <div className="col-12 d-flex justify-content-end">
            <Link to="edit">
              <Button type="primary">Edit site</Button>
            </Link>
          </div>
        </div>
      )}
    </>
  )
}
